import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import inventoryAPI from "../../api/inventory/inventoryApi";
import LocationSelect from "../../components/partials/inventory/common/LocationSelect";
import StockUpdateModal from "../../components/partials/inventory/StockUpdateModal";
import { useLoader } from "../../utils/LoaderUtils";
import { useSearchParams } from "react-router-dom";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";
import InventoryRow from "./InventoryRow";
import TableShimmer from "../../components/partials/TableShimmer";
// NEW: Barcode / QR label modal (BarcodeLabelModal.jsx isi folder mein rakho)
import BarcodeLabelModal from "./BarcodeLabelModal";

function InventoryListPage() {
  const [tableLoading, setTableLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({}); // NEW: cards ke counts ke liye
  const [pagination, setPagination] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [labelItem, setLabelItem] = useState(null); // NEW: jis item ka label dikhana hai

  const query = useMemo(
    () => ({
      wareHouseId: searchParams.get("wareHouseId") || "",
      search: searchParams.get("search") || "",
      status: searchParams.get("status") || "",
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
    }),
    [searchParams],
  );

  const searchTimeout = useRef();

  const updateFilter = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams);

      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }

      if (key !== "page") {
        params.set("page", 1);
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleSearch = (value) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateFilter("search", value.trim());
    }, 400);
  };

  // NEW: scanner SKU type karke Enter dabata hai -> 400ms wait kiye bina turant search
  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    clearTimeout(searchTimeout.current);
    updateFilter("search", e.target.value.trim());
    e.target.select(); // agla scan purane text ko replace kar de
  };

  const fetchData = async () => {
    setTableLoading(true);

    try {
      const res = await inventoryAPI.list(query);
      if (res.code === 200) {
        const { inventory, pagination, summary } = res.data;

        setItems(inventory || []);
        setPagination(pagination || {});
        setSummary(summary || {});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [query]);

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Inventory Details</h4>
              <h6>Manage stock across all warehouses</h6>
            </div>
          </div>
          <div className="page-btn">
            <button
              className="btn btn-primary"
              onClick={() => setModalOpen(true)}
            >
              <i className="ti ti-plus me-1" /> Update Stock
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-4 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-primary flex-shrink-0">
                    <i className="ti ti-building fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      Total SKUs
                    </p>
                    <h4>{summary?.totalSkus || 0}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-success flex-shrink-0">
                    <i className="ti ti-building fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      Low Stock
                    </p>
                    <h4>{summary?.lowStockCount || 0}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-danger flex-shrink-0">
                    <i className="ti ti-building fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      Out of Stock
                    </p>
                    <h4>{summary?.outOfStockCount || 0}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="welcome align-items-center justify-content-between">
          <div className="row">
            <div className="col-md-4">
              <label className="form-label small">Location</label>
              <LocationSelect
                value={searchParams.get("wareHouseId")}
                onChange={(e) => updateFilter("wareHouseId", e.target.value)}
                showAll
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small">Status</label>
              <select
                className="form-select"
                value={searchParams.get("status") || "all"}
                onChange={(e) => updateFilter("status", e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="in_stock">In Stock</option>
                <option value="low_stock">Low Stock</option>
                <option value="out_of_stock">Out of Stock</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small">Search / Scan SKU, Barcode</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="ti ti-scan" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search or scan SKU, barcode, or product name..."
                  defaultValue={query.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4>Inventory List</h4>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table">
                <thead className="thead-light">
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Location</th>
                    <th className="text-end">Available</th>
                    <th className="text-end">Alert At</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <TableShimmer rows={10} cols={6} />
                  ) : items.length > 0 ? (
                    items.map((item, index) => (
                      <InventoryRow
                        key={item._id}
                        index={index}
                        item={item}
                        page={query.page}
                        limit={query.limit}
                        setModalOpen={setModalOpen}
                        onShowLabel={setLabelItem} // NEW
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted p-4">
                        No inventory found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="row">
              <RowsPerPage
                value={searchParams.get("limit") || "10"}
                onChange={(val) => updateFilter("limit", val)}
              />

              <Pagination pagination={pagination} updateFilter={updateFilter} />
            </div>
          </div>
        </div>
      </div>

      <StockUpdateModal
        isOpen={modalOpen}
        mode="bulk"
        defaultLocationId={
          query.wareHouseId !== "all" ? query.wareHouseId : undefined
        }
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          setModalOpen(false);
          fetchData();
        }}
      />

      {/* NEW: Barcode / QR label modal — labelItem null hai to kuch render nahi hota */}
      <BarcodeLabelModal item={labelItem} onClose={() => setLabelItem(null)} />
    </>
  );
}

export default InventoryListPage;